/**
 * Parameter engine for repository goals.
 *
 * A repository skill stores its parameters as LADDER strings,
 * e.g. "2 items/ 4/ 6/ 8/ 10" or "10 mands/ 20/ 30/ 40/ 50". When the goal is
 * added to a child's IEP the clinician picks a value for each parameter — the
 * ladder steps are suggestions, any value (7, 9, …) is allowed — and that value
 * is woven INLINE into the goal statement:
 *
 *   text:  "Child should be able to do {} mands with 2 different therapists"
 *   param: "5 mands/ 10/ 15"  → picked 7
 *   =>     "Child should be able to do 7 mands with 2 different therapists"
 *
 * A parameter is woven in ONLY where the author put a placeholder, and WHAT is
 * inserted is the value exactly as typed - the whole parameter in the repository,
 * whatever the clinician chose on a child's IEP:
 *   0. NAMED slot bound to THIS parameter: "{1}".."{5}" bind Parameter 1..5,
 *      order-independent. "{Y}"/"{Z}" are aliases for 2/3, as is the legacy
 *      "(P1)/(P2)/(P3)" - goals authored before parameters 4 and 5 existed weave
 *      exactly as they did. "{X}" is positional, not an alias: see below.
 *   1. POSITIONAL placeholder — first unused slot: "{}" or "{x}" (legacy "()",
 *      "( )", "(#)", "___"). Repeating one fills the parameters in order.
 *
 * No placeholder, no substitution. The goal reads exactly as it was written and
 * the parameter stays in its own field. Nothing is ever appended to the
 * sentence: a trailing clause the author did not write is not the goal.
 */

// {} and {x} are the POSITIONAL slots: each occurrence takes the next parameter
// in order, so "{x} {x}" fills Parameter 1 then Parameter 2. The legacy forms
// "( )", "(#)" and "___" behave the same.
const PLACEHOLDER_RE = /\(\s*[#_.…]*\s*\)|_{2,}|\{\s*\}|\{\s*[xX]\s*\}/;

/** Parse a raw ladder string like "2 items/ 4/ 6/ 8/ 10" into a spec.
 *
 * The number is found ANYWHERE in a step, not only at its start. Real ladders
 * lead with words far more often than not - "For up to 2 / 5 / 7 / 10",
 * "for up to 5 seconds / 10 / 15" - and an anchored match dropped that first
 * rung entirely, so the goal defaulted to the SECOND step (5 instead of 2) and
 * the clinician saw a mastery criterion nobody had written.
 *
 * `unit` is the text after the number on the first step that has one ("items",
 * "seconds"); when the first step carries none, the last step is tried, because
 * a ladder often spells the noun only at the end ("… / 20 different instructions").
 * `prefix` is whatever leads the first step ("For up to"), kept so a caller can
 * show the rung as it was authored rather than as a bare digit.
 */
const LADDER_NUM_RE = /(\d+(?:\.\d+)?)/;

export const parseLadder = (raw) => {
  const segs = String(raw || '')
    .split('/')
    .map((x) => x.trim())
    .filter(Boolean);
  const ladder = [];
  segs.forEach((s) => {
    const m = s.match(LADDER_NUM_RE);
    if (m) ladder.push(m[1]);
  });

  let unit = '';
  let prefix = '';
  const splitStep = (step) => (step || '').match(/^(.*?)(\d+(?:\.\d+)?)\s*(.*)$/);
  const firstNumbered = segs.find((x) => LADDER_NUM_RE.test(x));
  const fm = splitStep(firstNumbered);
  if (fm) {
    prefix = fm[1].trim();
    unit = fm[3].trim();
  }
  if (!unit && segs.length > 1) {
    const lm = splitStep(segs[segs.length - 1]);
    if (lm) unit = lm[3].trim();
  }

  return { raw: String(raw || ''), numeric: ladder.length > 0, ladder, unit, prefix, options: segs };
};

/**
 * Weave chosen parameter values into the goal text.
 * @param {string} text - the goal template text
 * @param {Array<{meta: object, value: string}>} params - parsed specs + chosen values
 * @returns {{ segments: Array<{type: 'text'|'value', text: string}>, statement: string }}
 *   segments: ordered pieces for rendering (woven values highlighted);
 *   statement: the goal as plain text, with the placeholders filled in.
 */
export const weaveStatement = (text, params) => {
  const source = String(text || '');
  // Each substitution: { start, end, value } against the ORIGINAL text.
  const subs = [];
  let searchFrom = 0;

  let index = -1;
  for (const p of params || []) {
    index += 1;
    // Verbatim. The value is whatever the author or clinician typed, and it is
    // written into the slot unchanged - no unit added, no rung reassembled.
    const v = (p.value ?? '').toString().trim();
    if (!v) continue;

    // 0. NAMED slot for THIS parameter — binds Parameter 1..5 to its own slot,
    //    order-independent, and the form to prefer whenever a goal carries more
    //    than one parameter. {1}..{5} is canonical: it matches the Parameter 1..5
    //    columns on the import template, so what an author types in Excel and what
    //    they type in the dialog are the same thing. {X}/{Y}/{Z} and the legacy
    //    (P1)/(P2)/(P3) stay accepted for the first three, so every goal authored
    //    before parameters 4 and 5 existed keeps weaving unchanged.
    // {Y} and {Z} still bind Parameters 2 and 3 by name. {X} is deliberately NOT
    // here any more - it is positional (see PLACEHOLDER_RE), so "{x} {x}" fills
    // two parameters instead of aiming both at the first one.
    const letter = [null, 'Y', 'Z'][index];
    const slotAlts = [`\\(\\s*[Pp]\\s*${index + 1}\\s*\\)`, `\\{\\s*${index + 1}\\s*\\}`];
    if (letter) slotAlts.push(`\\{\\s*[${letter}${letter.toLowerCase()}]\\s*\\}`);
    const numbered = source.match(new RegExp(slotAlts.join('|')));
    if (numbered && typeof numbered.index === 'number') {
      const start = numbered.index;
      const end = start + numbered[0].length;
      subs.push({ start, end, value: v });
      searchFrom = Math.max(searchFrom, end);
      continue;
    }

    // 1. Positional placeholder "( )" / "___" / "{}" — first unused slot.
    const rest = source.slice(searchFrom);
    const ph = rest.match(PLACEHOLDER_RE);
    if (ph && typeof ph.index === 'number') {
      const start = searchFrom + ph.index;
      const end = start + ph[0].length;
      subs.push({ start, end, value: v });
      searchFrom = end;
      continue;
    }

    // No placeholder for this parameter — nothing is substituted, and nothing is
    // added to the end. The value is shown in its own field instead.
  }

  // Build ordered segments from the substitution list.
  subs.sort((a, b) => a.start - b.start);
  const segments = [];
  let cursor = 0;
  for (const s of subs) {
    if (s.start > cursor) segments.push({ type: 'text', text: source.slice(cursor, s.start) });
    segments.push({ type: 'value', text: s.value });
    cursor = s.end;
  }
  if (cursor < source.length) segments.push({ type: 'text', text: source.slice(cursor) });
  if (segments.length === 0) segments.push({ type: 'text', text: source });

  const statement = segments.map((s) => s.text).join('');

  return { segments, statement };
};

/**
 * Reverse of weaveStatement: recover the parameter values from a statement that
 * was already woven.
 *
 * A child's target stores only the finished sentence — the template it came
 * from and the values chosen for it are not persisted anywhere. So the Edit
 * dialog re-derives them: it walks the repository template, turns each slot
 * into a capture group and matches the saved sentence against it.
 *
 *   template:  "Complying with {1} one step instructions"
 *   statement: "Complying with 5 one step instructions"
 *   =>         values[0] === "5", slots === [0]
 *
 * @returns {{ values: string[], slots: number[], literal: number } | null}
 *   values: five entries, index n = Parameter n+1 ('' where the template has no
 *   slot for it); slots: which parameters the template actually references;
 *   literal: how much fixed text the template carries, so a caller comparing
 *   several candidate templates can prefer the most specific one. null = this
 *   template did not produce this sentence.
 */

// Slot forms, in the same precedence weaveStatement() writes them: named
// ({1}..{5}, {Y}/{Z}, legacy (P1)..(P3)) first, then the positional ones.
const SLOT_RE = /\(\s*[Pp]\s*([1-3])\s*\)|\{\s*([1-5])\s*\}|\{\s*([YyZz])\s*\}|\(\s*[#_.…]*\s*\)|_{2,}|\{\s*\}|\{\s*[xX]\s*\}/g;

// A capture that is itself still a slot means the clinician left that parameter
// empty, so weaveStatement never substituted it — an empty value, not a value.
const BARE_SLOT_RE = /^(\(\s*[Pp]\s*[1-3]\s*\)|\{\s*[1-5]\s*\}|\{\s*[YyZzxX]\s*\}|\(\s*[#_.…]*\s*\)|_{2,}|\{\s*\})$/;

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const matchStatement = (template, statement) => {
  const src = String(template || '').trim();
  const text = String(statement || '').trim();
  if (!src || !text) return null;

  // Pass 1 — every slot in author order, each bound to a parameter.
  const slots = [];
  SLOT_RE.lastIndex = 0;
  let m;
  while ((m = SLOT_RE.exec(src)) !== null) {
    let param = null;
    if (m[1])
      param = Number(m[1]) - 1; // (P1)..(P3)
    else if (m[2])
      param = Number(m[2]) - 1; // {1}..{5}
    else if (m[3]) param = m[3].toUpperCase() === 'Y' ? 1 : 2; // {Y} / {Z}
    slots.push({ start: m.index, end: m.index + m[0].length, param });
    if (m[0].length === 0) SLOT_RE.lastIndex += 1; // never spin on a zero-width match
  }

  if (slots.length === 0) {
    // No placeholder at all: weaving could not have changed the sentence, so an
    // exact text match still identifies the template — there is just nothing to
    // recover, every value having stayed in its own field.
    return src === text ? { values: ['', '', '', '', ''], slots: [], literal: src.length } : null;
  }

  // Positional slots take the first parameters no named slot claims, in order —
  // the rule weaveStatement() applies when it writes them.
  const named = new Set(slots.filter((s) => s.param !== null).map((s) => s.param));
  let nextFree = 0;
  slots.forEach((s) => {
    if (s.param !== null) return;
    while (named.has(nextFree)) nextFree += 1;
    s.param = nextFree;
    nextFree += 1;
  });

  // Pass 2 — the template as an anchored regex, one capture per slot.
  let pattern = '^';
  let literal = 0;
  let cursor = 0;
  slots.forEach((s) => {
    const lit = src.slice(cursor, s.start);
    literal += lit.trim().length;
    pattern += `${escapeRe(lit)}(.+?)`;
    cursor = s.end;
  });
  const tail = src.slice(cursor);
  literal += tail.trim().length;
  pattern += `${escapeRe(tail)}$`;

  let hit;
  try {
    hit = new RegExp(pattern, 'is').exec(text);
  } catch {
    return null; // a template that cannot be compiled simply does not match
  }
  if (!hit) return null;

  const values = ['', '', '', '', ''];
  slots.forEach((s, i) => {
    if (s.param > 4) return; // only Parameter 1..5 exist
    const v = (hit[i + 1] || '').trim();
    if (v && !BARE_SLOT_RE.test(v)) values[s.param] = v;
  });

  return { values, slots: [...new Set(slots.map((s) => s.param))].filter((i) => i <= 4).sort((a, b) => a - b), literal };
};

export default { parseLadder, weaveStatement, matchStatement };
